import React, { memo, useMemo } from 'react';
import { defaultTheme } from '@styles/theme.styled';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import Section from '@components/section';
import AnimatedElement from '@components/animated-element';
import { Wrapper } from '@components/wrapper.styled';
import {
  TitleContainer,
  ImageContainer,
  TextContent,
  FeatureWrapper,
  StyledImage,
  FeatureItemWrapper,
  FeatureListWrapper,
  FeatureListIcon,
  FeatureListDescription,
  IconWrapper,
  FeatureListColumn,
  FeatureColumnList,
  Divider,
} from './feature-list.styled';

export interface FeatureListsItemProps {
  image: ImageProps;
  description: {
    richText: RichTextBlock[];
  };
}

export interface FeatureListsProps {
  title?: string;
  image?: ImageProps;
  imageAlignment?: 'default' | 'left' | 'right';
  imageFit?: boolean;
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  'aria-label'?: string;
  lists: {
    document: {
      data: {
        first_title: {
          text: string;
        };
        second_title: {
          text: string;
        };
        first_column: FeatureListsItemProps[];
        second_column: FeatureListsItemProps[];
      };
    };
  };
}

const FeatureLists: React.FC<FeatureListsProps> = ({
  image,
  imageAlignment,
  imageFit,
  title,
  backgroundColor = 'white',
  lists,
  'aria-label': ariaLabel,
}) => {
  const hasImage = Boolean(image?.gatsbyImageData || image?.url);
  const hasTitle = Boolean(title);

  const FeatureList = useMemo(() => {
    return lists ? (
      <FeatureListWrapper>
        <FeatureListColumn>
          <Text
            type="title"
            stringText={lists.document.data.first_title.text}
            fontSize="heading6"
            margin="none"
          />
          <FeatureColumnList>
            {lists.document.data.first_column.map((item) => (
              <FeatureItemWrapper key={`feature-list-${item.description.richText[0].text || ''}`}>
                <IconWrapper>
                  <FeatureListIcon
                    url={item.image.url}
                    alt={item.image.alt}
                    gatsbyImageData={item.image.gatsbyImageData}
                    loading="lazy"
                    objectFit="contain"
                  />
                </IconWrapper>
                <FeatureListDescription>
                  <Text
                    type="description"
                    richText={item.description.richText}
                    margin="none"
                    fontSize="small"
                  />
                </FeatureListDescription>
              </FeatureItemWrapper>
            ))}
          </FeatureColumnList>
        </FeatureListColumn>
        <FeatureListColumn>
          <Text
            type="title"
            stringText={lists.document.data.second_title.text}
            fontSize="heading6"
            margin="none"
          />
          <FeatureColumnList>
            {lists.document.data.second_column.map((item) => (
              <FeatureItemWrapper key={`feature-list-${item.description.richText[0].text || ''}`}>
                <IconWrapper>
                  <FeatureListIcon
                    url={item.image.url}
                    alt={item.image.alt}
                    gatsbyImageData={item.image.gatsbyImageData}
                    loading="lazy"
                    objectFit="contain"
                  />
                </IconWrapper>
                <FeatureListDescription>
                  <Text
                    type="description"
                    richText={item.description.richText}
                    margin="none"
                    fontSize="small"
                  />
                </FeatureListDescription>
              </FeatureItemWrapper>
            ))}
          </FeatureColumnList>
        </FeatureListColumn>
      </FeatureListWrapper>
    ) : null;
  }, [lists]);

  return (
    <Section backgroundColor="white">
      <Wrapper>
        <FeatureWrapper
          bgColor={backgroundColor}
          role="article"
          aria-label={ariaLabel || title}
          $imageAlignment={imageAlignment}
        >
          {hasImage && (
            <ImageContainer>
              <StyledImage
                url={image?.url || ''}
                alt={image?.alt || title || 'Feature image'}
                gatsbyImageData={image?.gatsbyImageData}
                loading="lazy"
                objectPosition="0% 50%"
                objectFit={imageFit ? 'cover' : 'contain'}
              />
            </ImageContainer>
          )}
          <TextContent>
            {hasTitle && (
              <TitleContainer>
                <AnimatedElement
                  animationType="scaleX"
                  delay={0.125}
                  threshold={0.125}
                  rootMargin="50px"
                >
                  <Divider style={{ transformOrigin: '50% 0' }} />
                </AnimatedElement>
                {title && (
                  <Text
                    tag="h2"
                    type="title"
                    stringText={title}
                    fontSize="heading4"
                    margin="none"
                    fontColor="black"
                  />
                )}
              </TitleContainer>
            )}

            {lists && FeatureList}
          </TextContent>
        </FeatureWrapper>
      </Wrapper>
    </Section>
  );
};

export default memo(FeatureLists);
